import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react'
import styled from '@emotion/styled'
import { useIntl } from 'react-intl'
import {
  Alert,
  Button,
  ModalBody,
  ModalFooter,
  ModalHeader,
  RadioWithLabel,
  TextArea,
  spacing,
} from '@retailer-platform/shared-components'
import { Text } from '@retailer-platform/shared-components/src/tds'
import { useDashMessages } from '../../../../intl/intl.hooks'
import { useTrackEvent } from '../../../../utils/events/useTrackEvent.hook'
import { useSurvey } from '../RetailerSatisfactionSurveyContext'
import {
  type NavV3OptOutSurveyPages,
  type NavV3OptOutSurveyProps,
  type NavV3OptOutSurveyState,
} from './NavV3OptOutSurveyController'
import {
  NAV_V3_OPT_OUT_SURVEY_OPTIONS,
  type NavV3OptOutSurveyOptionValue,
} from './NavV3OptOutSurveyData'

const RadioGroup = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.X12,
  marginTop: spacing.X16,
})

const SubtitleWrapper = styled.div({
  marginTop: spacing.X8,
})

const PermanenceNoticeWrapper = styled.div({
  marginTop: spacing.X16,
  marginBottom: spacing.X8,
})

const TextAreaContainer = styled.div({
  marginTop: spacing.X16,
})

const FooterRow = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
})

export const NavV3OptOutSurveyContent = () => {
  const intl = useIntl()
  const trackEvent = useTrackEvent()
  const [submitted, setSubmitted] = useState(false)
  const { surveyState, updateSurveyState, submitSurvey, isSurveySubmitted, toolProps } = useSurvey<
    NavV3OptOutSurveyPages,
    NavV3OptOutSurveyState,
    NavV3OptOutSurveyProps
  >()

  const messages = useDashMessages({
    title: 'navV3.optOutSurvey.title',
    permanenceNotice: 'navV3.optOutSurvey.permanenceNotice',
    subtitle: 'navV3.optOutSurvey.subtitle',
    submit: 'navV3.optOutSurvey.submit',
    textAreaLabel: 'navV3.optOutSurvey.textAreaLabel',
  })

  const selectedOption = useMemo(
    () => NAV_V3_OPT_OUT_SURVEY_OPTIONS.find(opt => opt.value === surveyState.reason),
    [surveyState.reason]
  )

  const showTextArea = Boolean(selectedOption && !selectedOption.hideTextArea)
  const isSubmitDisabled = surveyState.reason === ''

  const onRadioChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value as NavV3OptOutSurveyOptionValue
      updateSurveyState('reason', value)
      // Clear additional context when changing reason so a stale draft from a
      // previous selection doesn't get submitted under a new reason.
      updateSurveyState('additionalContext', '')
    },
    [updateSurveyState]
  )

  const onTextAreaChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      updateSurveyState('additionalContext', e.currentTarget.value)
    },
    [updateSurveyState]
  )

  const onSubmit = useCallback(() => {
    trackEvent({
      id: 'app.navV3.optOutSurvey.submit',
      description: 'Tracks whenever the nav v3 opt-out survey is submitted',
      data: { reason: surveyState.reason },
    })
    submitSurvey()
    setSubmitted(true)
  }, [submitSurvey, surveyState.reason, trackEvent])

  // Close the modal once the submission round-trips successfully. Until then
  // the user sees the form (no separate confirmation page).
  useEffect(() => {
    if (submitted && isSurveySubmitted) {
      toolProps.onClose()
    }
  }, [submitted, isSurveySubmitted, toolProps])

  return (
    <>
      <ModalHeader useTDS>{messages.title}</ModalHeader>
      <ModalBody data-testid="nav-v3-opt-out-survey-content">
        <PermanenceNoticeWrapper>
          <Alert variant="info" data-testid="nav-v3-opt-out-permanence-notice">
            {messages.permanenceNotice}
          </Alert>
        </PermanenceNoticeWrapper>
        <SubtitleWrapper>
          <Text typography="bodyRegular">{messages.subtitle}</Text>
        </SubtitleWrapper>
        <RadioGroup role="radiogroup" aria-label={messages.title}>
          {NAV_V3_OPT_OUT_SURVEY_OPTIONS.map(option => (
            <RadioWithLabel
              key={option.value}
              id={`nav-v3-opt-out-radio-${option.value}`}
              data-testid={`nav-v3-opt-out-radio-${option.value}`}
              name="navV3OptOutReason"
              value={option.value}
              checked={surveyState.reason === option.value}
              onChange={onRadioChange}
              label={intl.formatMessage({ id: option.labelId })}
            />
          ))}
        </RadioGroup>
        {showTextArea && selectedOption?.textAreaPlaceholderId && (
          <TextAreaContainer>
            <TextArea
              data-testid="nav-v3-opt-out-text-area"
              aria-label={messages.textAreaLabel}
              placeholder={intl.formatMessage({ id: selectedOption.textAreaPlaceholderId })}
              value={surveyState.additionalContext}
              onChange={onTextAreaChange}
              css={{ minHeight: 96, fontSize: 14 }}
              autoFocus
            />
          </TextAreaContainer>
        )}
      </ModalBody>
      <ModalFooter>
        <FooterRow>
          <Button
            data-testid="nav-v3-opt-out-submit"
            disabled={isSubmitDisabled}
            onClick={onSubmit}
          >
            {messages.submit}
          </Button>
        </FooterRow>
      </ModalFooter>
    </>
  )
}

export default NavV3OptOutSurveyContent
